import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Settings, GripVertical, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface WidgetConfig {
  id: string;
  type: 'kpi' | 'chart' | 'table' | 'custom' | 'custom-table' | 'custom-chart';
  title: string;
  size: 'small' | 'medium' | 'large' | 'full';
  config?: {
    chartType?: string;
    color?: string;
    customData?: {
      name: string;
      columns: Array<{ id: string; name: string; type: 'text' | 'number' | 'date' }>;
      rows: Array<Record<string, any>>;
    };
    [key: string]: any;
  };
}

interface DraggableWidgetProps {
  widget: WidgetConfig;
  children: React.ReactNode;
  isEditMode: boolean;
  onConfigure?: (widget: WidgetConfig) => void;
  onRemove?: (widgetId: string) => void;
  isDragging?: boolean;
  forceFullWidth?: boolean;
}

export const DraggableWidget: React.FC<DraggableWidgetProps> = ({
  widget,
  children,
  isEditMode,
  onConfigure,
  onRemove,
  isDragging: isExternallyDragging = false,
  forceFullWidth = false
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ 
    id: widget.id,
    disabled: !isEditMode
  });

  const isDragging = isSortableDragging || isExternallyDragging;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const getSizeClasses = (size: string) => {
    if (forceFullWidth) {
      return 'col-span-full';
    }
    switch (size) {
      case 'small':
        return 'col-span-1';
      case 'medium':
        return 'col-span-1 md:col-span-2';
      case 'large':
        return 'col-span-1 md:col-span-2 lg:col-span-3';
      case 'full':
        return 'col-span-full';
      default:
        return 'col-span-1';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        '--widget-color': widget.config?.color || 'hsl(var(--primary))'
      } as React.CSSProperties}
      className={cn(
        getSizeClasses(widget.size),
        'relative group transition-all duration-200',
        isDragging && 'opacity-30 scale-95',
        !isDragging && 'hover:scale-[1.02]',
        isEditMode && 'ring-2 ring-primary/20 hover:ring-primary/40'
      )}
      {...attributes}
    >
      {/* Edit Mode Overlay */}
      {isEditMode && widget.id !== 'add-widget' && (
        <div className="absolute inset-0 bg-black/5 backdrop-blur-[1px] z-10 rounded-lg border-2 border-dashed border-primary/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200">
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              {...listeners}
              className="cursor-grab active:cursor-grabbing bg-white/90 hover:bg-white shadow-lg transform hover:scale-105 transition-transform"
            >
              <GripVertical className="w-4 h-4" />
            </Button>
            {onConfigure && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onConfigure(widget)}
                className="bg-white/90 hover:bg-white shadow-lg transform hover:scale-105 transition-transform"
              >
                <Settings className="w-4 h-4" />
              </Button>
            )}
            {onRemove && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => onRemove(widget.id)}
                className="bg-red-500/90 hover:bg-red-500 shadow-lg transform hover:scale-105 transition-transform"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Widget Content */}
      <div className={cn(
        'h-full',
        isEditMode && widget.id !== 'add-widget' && 'pointer-events-none'
      )}>
        {children}
      </div>
    </div>
  );
};